import type { UseQueryOptions } from "@tanstack/react-query";
import {
  apiRequest,
  type ApiError,
  useApiQuery,
  useApiMutation,
} from "@/shared/api";
import type {
  CryptoDepositAddress,
  CryptoWithdrawalDetails,
  CryptoWithdrawalFeeResponse,
  CreateCryptoWithdrawalPayload,
  CreateCryptoWithdrawalResponse,
} from "../interfaces/crypto.interface";

type ApiQueryOptions<TData> = Omit<
  UseQueryOptions<TData, ApiError, TData, readonly unknown[]>,
  "queryKey" | "queryFn"
>;

const cryptoDepositAddressesRequest = {
  url: "/crypto/deposit/address",
  method: "get" as const,
};

export const getCryptoDepositAddresses = () =>
  apiRequest<CryptoDepositAddress[]>(cryptoDepositAddressesRequest);

export const useCryptoDepositAddresses = (
  options?: ApiQueryOptions<CryptoDepositAddress[]>
) =>
  useApiQuery<CryptoDepositAddress[], ApiError>(
    ["crypto-deposit-addresses"],
    cryptoDepositAddressesRequest,
    options
  );

const cryptoWithdrawalDetailsRequest = (withdrawalId: string) => ({
  url: `/crypto/withdrawal/${withdrawalId}`,
  method: "get" as const,
});

export const getCryptoWithdrawalDetails = (withdrawalId: string) =>
  apiRequest<CryptoWithdrawalDetails>(
    cryptoWithdrawalDetailsRequest(withdrawalId)
  );

export const useCryptoWithdrawalDetails = (
  withdrawalId: string,
  options?: ApiQueryOptions<CryptoWithdrawalDetails>
) => {
  const mergedOptions: ApiQueryOptions<CryptoWithdrawalDetails> = {
    ...(options ?? {}),
    enabled:
      Boolean(withdrawalId) &&
      (options?.enabled !== undefined ? options.enabled : true),
  };

  return useApiQuery<CryptoWithdrawalDetails, ApiError>(
    ["crypto-withdrawal-details", withdrawalId],
    cryptoWithdrawalDetailsRequest(withdrawalId),
    mergedOptions
  );
};

const cryptoWithdrawalFeeRequest = (params: {
  currency: string;
  network?: string;
}) => ({
  url: "/crypto/withdrawal/fee",
  method: "get" as const,
  params,
});

export const getCryptoWithdrawalFee = (params: {
  currency: string;
  network?: string;
}) =>
  apiRequest<CryptoWithdrawalFeeResponse>(cryptoWithdrawalFeeRequest(params));

export const useCryptoWithdrawalFee = (
  params: { currency: string; network?: string },
  options?: ApiQueryOptions<CryptoWithdrawalFeeResponse>
) => {
  const normalizedCurrency = params.currency?.trim();
  const queryKey = [
    "crypto-withdrawal-fee",
    normalizedCurrency?.toLowerCase(),
    params.network?.trim().toLowerCase() ?? null,
  ] as const;

  const mergedOptions: ApiQueryOptions<CryptoWithdrawalFeeResponse> = {
    ...(options ?? {}),
    enabled:
      Boolean(normalizedCurrency) &&
      (options?.enabled !== undefined ? options.enabled : true),
  };

  return useApiQuery<CryptoWithdrawalFeeResponse, ApiError>(
    queryKey,
    cryptoWithdrawalFeeRequest({
      currency: normalizedCurrency,
      network: params.network?.trim(),
    }),
    mergedOptions
  );
};

const createCryptoWithdrawalRequest = (
  payload: CreateCryptoWithdrawalPayload
) => {
  const normalizedCurrency = payload.currency.trim();
  const normalizedNetwork =
    normalizedCurrency.toUpperCase() === "USDC"
      ? payload.network?.trim()
      : undefined;

  const data: CreateCryptoWithdrawalPayload = {
    currency: normalizedCurrency,
    amount: payload.amount,
    walletAddress: payload.walletAddress,
    memo: payload.memo,
    network: normalizedNetwork,
  };

  return {
    url: "/crypto/withdrawal",
    method: "post" as const,
    data,
  };
};

export const useCreateCryptoWithdrawal = () =>
  useApiMutation<CreateCryptoWithdrawalResponse, CreateCryptoWithdrawalPayload>(
    createCryptoWithdrawalRequest
  );
